

"use client";

import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import type { Product, CartItem, ProductVariant, ProductExpress, DeliverySettings } from '@/lib/types';
import { toast } from '@/hooks/use-toast';
import { onAuthStateChanged, User } from 'firebase/auth';
import { auth, db } from '@/lib/firebase';
import {
  collection,
  query,
  onSnapshot,
  doc,
  getDoc,
  writeBatch,
  addDoc,
  deleteDoc,
  where,
  getDocs,
  serverTimestamp,
  updateDoc
} from 'firebase/firestore';
import NoWarrantyAlert from '@/components/shared/NoWarrantyAlert';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from '@/components/ui/alert-dialog';


interface ExpressCartContextType {
  cartItems: CartItem[];
  addToCart: (product: Product, quantity: number, variant?: ProductVariant, partnerId?: string) => void;
  removeFromCart: (productId: string, variantId?: string) => void;
  updateQuantity: (productId: string, quantity: number, variantId?: string) => void;
  clearCart: () => void;
  getItemStock: (item: CartItem) => number;
}

const ExpressCartContext = createContext<ExpressCartContextType | undefined>(undefined);

const CART_EXPIRATION_TIME_MS = 24 * 60 * 60 * 1000; // 24 hours

export const ExpressCartProvider = ({ children }: { children: ReactNode }) => {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [deliverySettings, setDeliverySettings] = useState<DeliverySettings | null>(null);
  const [showClosedDialog, setShowClosedDialog] = useState(false);
  const router = useRouter();
  
  // State for no-warranty alert
  const [noWarrantyAlert, setNoWarrantyAlert] = useState<{ product: Product, quantity: number, variant?: ProductVariant, partnerId?: string } | null>(null);

  const proceedAddToCart = useCallback(async (product: Product, quantity: number, variant?: ProductVariant, partnerId?: string) => {
    if (!currentUser) {
        toast({ variant: 'destructive', title: 'Login Necessário', description: 'Você precisa estar logado para adicionar itens.' });
        return;
    }
    if (!variant) {
        toast({ variant: 'destructive', title: 'Erro', description: 'Uma variação do produto deve ser selecionada.' });
        return;
    }

    let actualPartnerId = partnerId;
    let availableStock = 0;

    // If it's a general 'wolf-express' add, find the first partner with stock.
    if (partnerId === 'wolf-express') {
        const partnerStocks = variant.partnerStock || {};
        const firstAvailablePartner = Object.keys(partnerStocks).find(pId => partnerStocks[pId] > 0);
        
        if (firstAvailablePartner) {
            actualPartnerId = firstAvailablePartner;
            availableStock = partnerStocks[firstAvailablePartner];
        } else {
             toast({ variant: "destructive", title: "Estoque Esgotado", description: "Este item está esgotado em todos os parceiros Express." });
             return;
        }
    } else if (partnerId) {
        const expressDocRef = doc(db, 'productExpress', `${product.id}_${variant.id}_${partnerId}`);
        const docSnap = await getDoc(expressDocRef);
        if (docSnap.exists()) {
          availableStock = (docSnap.data() as ProductExpress).stock || 0;
        }
    }

    if (!actualPartnerId) {
         toast({ variant: "destructive", title: "Parceiro não encontrado", description: "Não foi possível identificar o parceiro para este item." });
         return;
    }

    const itemIdentifier = variant.id;
    const existingItem = cartItems.find(item => item.variantId === itemIdentifier && item.addedFrom === actualPartnerId);
    const currentQuantityInCart = existingItem?.quantity || 0;
    
    if (currentQuantityInCart + quantity > availableStock) {
        toast({
            variant: "destructive",
            title: "Estoque Insuficiente",
            description: `Apenas ${availableStock} unidades disponíveis no estoque Express.`
        });
        return;
    }
    
    try {
        const cartCollectionRef = collection(db, `users/${currentUser.uid}/cartExpress`);

        // Check for and remove expired items before adding a new one
        const now = Date.now();
        const batch = writeBatch(db);
        const snapshot = await getDocs(cartCollectionRef);
        let itemsRemoved = 0;
        let existingPartnerIdInCart: string | undefined = undefined;
        snapshot.docs.forEach(doc => {
            const itemData = doc.data();
            const itemTimestamp = (itemData.createdAt as any)?.toMillis();
            if (itemTimestamp && now - itemTimestamp > CART_EXPIRATION_TIME_MS) {
                batch.delete(doc.ref);
                itemsRemoved++;
            }
            if (!existingPartnerIdInCart) {
                existingPartnerIdInCart = itemData.addedFrom;
            }
        });
        
        // Enforce single partner rule
        if (existingPartnerIdInCart && actualPartnerId !== existingPartnerIdInCart) {
            toast({
                variant: "destructive",
                title: "Carrinho Express",
                description: "Você só pode adicionar produtos de um parceiro Express por vez. Limpe seu carrinho para continuar."
            });
            return;
        }

        await batch.commit();
        if (itemsRemoved > 0) {
            toast({ title: 'Carrinho Atualizado', description: 'Alguns itens expirados foram removidos do seu carrinho.' });
        }


        if (existingItem && existingItem.docId) {
            const itemDocRef = doc(db, `users/${currentUser.uid}/cartExpress`, existingItem.docId);
            await updateDoc(itemDocRef, { quantity: existingItem.quantity + quantity });
        } else {
             const newItem: StoredCartItem = {
                productId: product.id,
                variantId: variant.id,
                quantity: quantity,
                addedFrom: actualPartnerId,
                createdAt: serverTimestamp() // Add timestamp
            };
            await addDoc(cartCollectionRef, newItem);
        }
        
        toast({
            title: "Produto Adicionado!",
            description: `${product.name} (${variant.options.name}) foi adicionado ao carrinho express.`,
        });

    } catch (error) {
        console.error("Error adding to Firestore cart:", error);
        toast({ variant: 'destructive', title: 'Erro', description: 'Não foi possível adicionar ao carrinho.' });
    }
  }, [cartItems, currentUser]);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    let unsubscribeSettings: (() => void) | undefined;

    const deliverySettingsRef = doc(db, 'settings', 'delivery');
    unsubscribeSettings = onSnapshot(deliverySettingsRef, (doc) => {
        setDeliverySettings(doc.data() as DeliverySettings);
    });
    
    const authUnsubscribe = onAuthStateChanged(auth, (user) => {
      if (unsubscribe) {
        unsubscribe(); // Cleanup previous listener
      }
      setCurrentUser(user);

      if (user) {
        const cartQuery = query(collection(db, `users/${user.uid}/cartExpress`));
        unsubscribe = onSnapshot(cartQuery, async (snapshot) => {
          const storedItems = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as StoredCartItem & {id: string}));
          const reconstructed = await reconstructCart(storedItems);
          setCartItems(reconstructed);
        }, (error) => {
          console.error("Error listening to express cart:", error);
        });

      } else {
        setCartItems([]); // Clear cart on logout
      }
    });

    return () => {
      authUnsubscribe();
      if (unsubscribe) {
        unsubscribe();
      }
       if (unsubscribeSettings) {
        unsubscribeSettings();
      }
    };
  }, []);

  useEffect(() => {
    if (currentUser) {
        const pendingItemJSON = localStorage.getItem('pendingExpressCartItem');
        if (pendingItemJSON) {
            const reconstructAndAdd = async () => {
                try {
                    const pendingItem = JSON.parse(pendingItemJSON);
                    const productDoc = await getDoc(doc(db, 'products', pendingItem.productId));
                    if (productDoc.exists()) {
                        const product = { id: productDoc.id, ...productDoc.data() } as Product;
                        const variant = pendingItem.variantId ? product.variants?.find(v => v.id === pendingItem.variantId) : undefined;
                        
                        proceedAddToCart(product, pendingItem.quantity, variant, pendingItem.partnerId);
                    }
                } catch (e) {
                    console.error("Error adding pending express item to cart", e);
                } finally {
                    localStorage.removeItem('pendingExpressCartItem');
                }
            };
            reconstructAndAdd();
        }
    }
  }, [currentUser, proceedAddToCart]);


  const reconstructCart = async (storedItems: (StoredCartItem & {id: string})[]): Promise<CartItem[]> => {
    if (storedItems.length === 0) return [];

    try {
        const productIds = Array.from(new Set(storedItems.map(item => item.productId)));
        if (productIds.length === 0) return [];

        const fetchedProducts = new Map<string, Product>();
        const productsRef = collection(db, 'products');
        
        const idChunks: string[][] = [];
        for (let i = 0; i < productIds.length; i += 30) {
            idChunks.push(productIds.slice(i, i + 30));
        }

        const queryPromises = idChunks.map(chunk => getDocs(query(productsRef, where('__name__', 'in', chunk))));
        const querySnapshots = await Promise.all(queryPromises);

        querySnapshots.forEach(snapshot => {
            snapshot.forEach(doc => fetchedProducts.set(doc.id, { id: doc.id, ...doc.data() } as Product));
        });

        const reconstructedItems: CartItem[] = [];
        for (const item of storedItems) {
            const product = fetchedProducts.get(item.productId);
            if (product && item.variantId && item.addedFrom) {
                const variant = product.variants?.find(v => v.id === item.variantId);
                
                const expressDocRef = doc(db, 'productExpress', `${product.id}_${item.variantId}_${item.addedFrom}`);
                const expressDocSnap = await getDoc(expressDocRef);
                
                if (expressDocSnap.exists()) {
                    const expressData = expressDocSnap.data() as ProductExpress;
                    reconstructedItems.push({
                        ...product,
                        docId: item.id, // Firestore document ID
                        variants: product.variants ?? [],
                        quantity: item.quantity,
                        price: expressData.price, // Use the price from productExpress
                        costPrice: variant?.costPrice, // Get cost price from the variant of the base product
                        stock: expressData.stock || 0, // This is the specific partner's stock
                        imageUrl: variant?.imageUrl || product.imageUrl,
                        variantId: item.variantId,
                        id: product.id,
                        addedFrom: item.addedFrom,
                        originalPrice: expressData.originalPrice,
                        noWarranty: product.noWarranty,
                    });
                }
            }
        }
        return reconstructedItems;
    } catch (error) {
        console.error("Error reconstructing cart:", error);
        return [];
    }
  }

  const isExpressOpen = useCallback(() => {
    if (!deliverySettings || !deliverySettings.wolfExpressEnabled) return false;

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const [startHour, startMinute] = (deliverySettings.wolfExpressStartTime || '00:00').split(':').map(Number);
    const startTime = startHour * 60 + startMinute;

    const [endHour, endMinute] = (deliverySettings.wolfExpressEndTime || '23:59').split(':').map(Number);
    const endTime = endHour * 60 + endMinute;

    return currentTime >= startTime && currentTime <= endTime;
  }, [deliverySettings]);


  const getItemStock = (item: CartItem): number => {
    // This stock is now pre-fetched and represents the specific partner's stock.
    return item.stock || 0;
  };

  const addToCart = async (product: Product, quantity: number, variant?: ProductVariant, partnerId?: string) => {
    const user = auth.currentUser;
    if (!user) {
        const itemToAdd = {
            productId: product.id,
            quantity,
            variantId: variant?.id,
            partnerId
        };
        localStorage.setItem('pendingExpressCartItem', JSON.stringify(itemToAdd));
        router.push('/login?redirect=/cartexpress');
        return;
    }

    if (!isExpressOpen()) {
        setShowClosedDialog(true);
        return;
    }
    if (product.noWarranty) {
      setNoWarrantyAlert({ product, quantity, variant, partnerId });
      return;
    }
    proceedAddToCart(product, quantity, variant, partnerId);
  };
  
  const removeFromCart = async (productId: string, variantId?: string) => {
    if (!currentUser) return;

    const itemToRemove = cartItems.find(item => item.id === productId && item.variantId === variantId);
    if (itemToRemove && itemToRemove.docId) {
        try {
            await deleteDoc(doc(db, `users/${currentUser.uid}/cartExpress`, itemToRemove.docId));
        } catch(error) {
            console.error("Error removing from cart:", error);
        }
    }
  };

  const updateQuantity = async (productId: string, quantity: number, variantId?: string) => {
    if (!currentUser) return;

    const itemToUpdate = cartItems.find(item => item.id === productId && item.variantId === variantId);
    if (!itemToUpdate || !itemToUpdate.docId) return;

    if (quantity <= 0) {
      await removeFromCart(productId, variantId);
      return;
    }
    
    const stock = getItemStock(itemToUpdate);
    if (quantity > stock) {
        toast({ variant: 'destructive', title: 'Estoque Insuficiente', description: `Máximo de ${stock} unidades em estoque.` });
        quantity = stock;
    }

    try {
        const itemDocRef = doc(db, `users/${currentUser.uid}/cartExpress`, itemToUpdate.docId);
        await updateDoc(itemDocRef, { quantity: quantity });
    } catch(error) {
        console.error("Error updating quantity:", error);
    }
  };

  const clearCart = async () => {
    if (!currentUser) return;
    try {
        const cartCollectionRef = collection(db, `users/${currentUser.uid}/cartExpress`);
        const snapshot = await getDocs(cartCollectionRef);
        const batch = writeBatch(db);
        snapshot.docs.forEach(doc => batch.delete(doc.ref));
        await batch.commit();
    } catch (error) {
        console.error("Error clearing cart:", error);
    }
  };

  return (
    <ExpressCartContext.Provider value={{ cartItems, addToCart, removeFromCart, updateQuantity, clearCart, getItemStock }}>
      {children}
      <AlertDialog open={showClosedDialog} onOpenChange={setShowClosedDialog}>
          <AlertDialogContent>
              <AlertDialogHeader>
                  <AlertDialogTitle>Wolf Express Fechado</AlertDialogTitle>
                  <AlertDialogDescription>
                      O nosso serviço de entrega express está disponível das {deliverySettings?.wolfExpressStartTime} às {deliverySettings?.wolfExpressEndTime}. Por favor, tente novamente durante este horário.
                  </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                  <AlertDialogAction onClick={() => setShowClosedDialog(false)}>Entendi</AlertDialogAction>
              </AlertDialogFooter>
          </AlertDialogContent>
      </AlertDialog>
      <NoWarrantyAlert 
        isOpen={!!noWarrantyAlert}
        onClose={() => setNoWarrantyAlert(null)}
        onConfirm={() => {
            if (noWarrantyAlert) {
                proceedAddToCart(noWarrantyAlert.product, noWarrantyAlert.quantity, noWarrantyAlert.variant, noWarrantyAlert.partnerId);
                setNoWarrantyAlert(null);
            }
        }}
      />
    </ExpressCartContext.Provider>
  );
};

export const useExpressCart = () => {
  const context = useContext(ExpressCartContext);
  if (context === undefined) {
    throw new Error('useExpressCart must be used within a ExpressCartProvider');
  }
  return context;
};

type StoredCartItem = {
  productId: string;
  variantId?: string;
  quantity: number;
  addedFrom?: string; 
  createdAt?: any;
};
